import {PromocodesService} from "@/services/PromocodesService/PromocodesService";
import {EventBasesServiceInterface} from "@/services/EventBasesService/EventBasesService.interface";
import {
    DetermineLinkActionsParams,
    LinkAction,
    SelectedEventsType
} from "@/views/Admin/Events/Event/Promocodes/AttachEventsModal/AttachEventsModal.interface";
import {TicketInterface} from "@/types/Ticket.interface";

export const getUniqueEvents = (events: EventBasesServiceInterface[]) => {
    return events.filter((event, index, self) =>
        self.findIndex(e => e.id === event.id) === index
    );
};

export const handlePrepareParams = <T extends Record<string, unknown>>(filters: T): Partial<T> => {
    const params: Partial<T> = {};

    for (const key in filters) {
        const value = filters[key];
        if (value !== null && value !== '' && value !== undefined) {
            params[key] = value;
        }
    }

    return params;
};

export const classifyEvents = (
    events: EventBasesServiceInterface[],
    initialEventIds: number[],
    relatedEventIds: Set<number>
) => {
    return events.reduce(
        (acc, event) => {
            if (initialEventIds.includes(event.eventId)) {
                acc.initial.push(event);
            } else if (relatedEventIds.has(event.eventId)) {
                acc.related.push(event);
            } else {
                acc.regular.push(event);
            }
            return acc;
        },
        { initial: [], related: [], regular: [] }
    );
};


export const prepareMapCollections = (baseEvents: EventBasesServiceInterface[], related: SelectedEventsType) => {
    return {
        relatedMap: new Map(Object.entries(related)),
        baseEventsMap: new Map(baseEvents.map(e => [e.eventId, e]))
    };
};

export const getEventState = (baseEvent: EventBasesServiceInterface, tickets: TicketInterface[], relatedMap: Map<string, Record<string, boolean>>, eventId: number) => {
    const allTicketIds = baseEvent.tickets.map((t: TicketInterface) => t.ticketId);

    const isSelectedFullEvent = tickets
        ? baseEvent.tickets.length === Object.keys(tickets).length && allTicketIds.every(id => tickets[id])
        : false;

    const isRelatedEventSelected = relatedMap.has(String(eventId));

    const relatedTickets = isRelatedEventSelected ? relatedMap.get(String(eventId)) : {};

    return {isSelectedFullEvent, isRelatedEventSelected, relatedTickets, allTicketIds};
};

const createLinkAction = (
    type: 'event' | 'ticket',
    action: 'link' | 'unlink',
    eventId: number,
    ticketId?: number
): LinkAction => {
    const result: LinkAction = {type, action, eventId};
    if (type === 'ticket' && ticketId !== undefined) {
        result.ticketId = ticketId;
    }
    return result;
}

export const determineLinkActions = (params: DetermineLinkActionsParams): LinkAction[] => {
    const {
        eventId,
        tickets,
        isSelectedFullEvent,
        isRelatedEventSelected,
        relatedTickets,
        allTicketIds,
        initialPromocodeEventId
    } = params;

    // 1. СОБЫТИЕ ВЫБРАНО ЦЕЛИКОМ И НЕ ПРИВЯЗАНО
    if (isSelectedFullEvent && !isRelatedEventSelected) {
        if (initialPromocodeEventId.includes(eventId)) {
            return []
        }
        return [createLinkAction('event', 'link', eventId)];
    }

    // 2. СОБЫТИЕ ОТЖАТО И ПРИВЯЗАНО (все билеты отжаты)
    if (isRelatedEventSelected && Object.values(tickets).every(isSelected => !isSelected)) {
        // Проверяем, привязано ли событие целиком (все билеты)
        const isFullyLinked = Object.keys(relatedTickets).length === allTicketIds.length;

        if (initialPromocodeEventId.includes(eventId) && isFullyLinked) {
            return []
        }

        if (isFullyLinked) {
            return [createLinkAction('event', 'unlink', eventId)];
        } else {
            const actions: LinkAction[] = [];

            Object.keys(tickets).forEach(ticketId => {
                if (relatedTickets[ticketId]) {
                    actions.push(createLinkAction('ticket', 'unlink', eventId, Number(ticketId)));
                }
            });

            actions.push(createLinkAction('event', 'unlink', eventId))

            return actions;
        }
    }

    const actions: LinkAction[] = [];
    const selectedTicketIds: string[] = [];

    // Обработка отдельных билетов
    Object.entries(tickets).forEach(([ticketId, isSelected]) => {
        const isTicketRelated = relatedTickets[ticketId];

        // 3. БИЛЕТ ВЫБРАН И НЕ ПРИВЯЗАН
        if (isSelected && !isTicketRelated && !isSelectedFullEvent) {
            actions.push(createLinkAction('ticket', 'link', eventId, Number(ticketId)));
            selectedTicketIds.push(ticketId);
        }
        // 4. БИЛЕТ ОТЖАТ И ПРИВЯЗАН
        else if (!isSelected && isTicketRelated && !isSelectedFullEvent) {
            actions.push(createLinkAction('ticket', 'unlink', eventId, Number(ticketId)));
        }
    });

    // Дополнительная логика для условия 3 (если нет связанного события)
    if (selectedTicketIds.length > 0 && !isRelatedEventSelected) {
        actions.push(createLinkAction('event', 'link', eventId));
    }

    // 5. СОБЫТИЕ ПРИВЯЗАНО ЦЕЛИКОМ И ВЫБРАНЫ НЕ ВСЕ БИЛЕТЫ
    if (isRelatedEventSelected &&
        !isSelectedFullEvent &&
        Object.keys(relatedTickets).length === allTicketIds.length) {

        return Object.entries(tickets)
            .filter(([_, isSelected]) => isSelected)
            .map(([ticketId]) => createLinkAction('ticket', 'link', eventId, Number(ticketId)));
    }

    // 6. ПЕРЕХОД С ЧАСТИЧНЫХ БИЛЕТОВ НА ПОЛНОЕ СОБЫТИЕ
    if (isSelectedFullEvent &&
        isRelatedEventSelected &&
        Object.keys(relatedTickets).length < allTicketIds.length) {

        const actions: LinkAction[] = [];

        // Отвязываем ранее привязанные билеты
        Object.entries(relatedTickets)
            .filter(([_, isSelected]) => isSelected)
            .forEach(([ticketId]) => {
                actions.push(createLinkAction('ticket', 'unlink', eventId, Number(ticketId)));
            });

        return actions;
    }

    return actions;
};

export const processPromocodeLinkActions = (actions: LinkAction[], promocodeId: number | number[]) => {
    const promocodeIds = Array.isArray(promocodeId) ? promocodeId : [promocodeId];

    return actions.map(action => {
        return promocodeIds.map(promocodeId => {
                switch (action.type) {
                    case 'event':
                        return action.action === 'link'
                            ? PromocodesService.Event.Attach(Number(action.eventId), promocodeId)
                            : PromocodesService.Event.Detach(Number(action.eventId), promocodeId);
                    case 'ticket':
                        return action.action === 'link'
                            ? PromocodesService.Tickets.Attach(action.ticketId, promocodeId)
                            : PromocodesService.Tickets.Detach(action.ticketId, promocodeId);
                    default:
                        return Promise.resolve();
                }
            }
        )
    })
}

